/**
 * A short, synthesized two-tone chime for a watched resource's status
 * change — no bundled audio asset needed, and no dependency on autoplay
 * permissions beyond the same user-gesture requirement any audio has.
 *
 * Browser push notifications (the closed-tab path, see notifications/push.ts)
 * have no `sound` option in the spec at all — this is the richer alternative
 * for whenever a tab is actually open, foreground or not.
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx ??= new Ctor();
  return ctx;
}

/**
 * `direction` picks which way the two tones move: down for something going
 * wrong (the default, and what a watched resource's health change has always
 * used), up for something coming back. A live ping trace fires both, often
 * minutes apart with nobody looking at the screen — the pitch is what tells
 * them which one it was without having to look.
 */
export function playAlertChime(direction: 'down' | 'up' = 'down'): void {
  const audio = getCtx();
  if (!audio) return;
  try {
    if (audio.state === 'suspended') void audio.resume();
    const start0 = audio.currentTime;
    for (const [i, freq] of (direction === 'up' ? [660, 880] : [880, 660]).entries()) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = start0 + i * 0.14;
      // Quick attack, exponential decay — a "ding", not a tone that lingers
      // and stacks awkwardly if several transitions land close together.
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
      osc.connect(gain).connect(audio.destination);
      osc.start(start);
      osc.stop(start + 0.32);
    }
  } catch {
    // Autoplay policy blocked it, or the browser doesn't really support Web
    // Audio despite the constructor existing — the push notification is the
    // path that has to work; this is a bonus.
  }
}
